// InputFormAI.jsx
import React, { useState, useEffect } from "react";

import Alerts from "../systemUtils/Alerts";
import UploadDocument from "./UploadDocument";
import InputFormAIInformationBox from "./inputFormAIHelpers/InputFormAIInformationBox";
import DynamicFormSection from "./inputFormAIHelpers/DynamicFormSection";
import OutputScreen from "../systemUtils/OutputScreen";
import { navigateNext, handleCancel } from "../functions/InputFormFunctions";
import { getFunctions, httpsCallable } from "firebase/functions";
import LoadingCircle from "../../utils/LoadingCircle"; // Adjust the path as necessary
import StatusScreen from "../systemUtils/StatusScreen"; // Import StatusScreen
import logUserAction from "../functions/AuditTrail"; // Import logUserAction (adjust the path accordingly)
import { useDispatch, useSelector } from "react-redux";
import { setLastWrittenUid } from "../../../redux/actions";
import store from "../../../redux/store";
import { db } from "../../../firebase";
import { collection, query, where, getDocs } from "firebase/firestore";

import "./InputFormAI.css";

// Initialize Firebase Functions (Cloud Functions V2)
const functions = getFunctions();

const InputFormAI = ({
  sections,
  initialFormData,
  company,
  collectionBrand,
  systemName,
  uniqueId,
  isNew,
  checkDuplicateFields = [],
  validationRules = {},
  dataPointDefinitions,
  userEmail,
  approvalGate,
  onComplete,
  duplicates,
  extractDataPoints = () => {}, // New prop to pass a function and get the data back.
}) => {
  const dispatch = useDispatch();
  /* ───────── DEBUG: watch heroUser slice ───────── */
  const lastWrittenUidInState = useSelector((s) => s.lastWrite.uid);

  React.useEffect(() => {
    console.log(
      "[InputFormAI] heroUser.lastWrittenUid changed →",
      lastWrittenUidInState
    );
  }, [lastWrittenUidInState]); // runs every time it changes
  /* ────────────────────────────────────────────── */

  const [formData, setFormData] = useState(initialFormData);
  const [resolvedFields, setResolvedFields] = useState([]); // Tracks resolved fields
  const [currentSectionIndex, setCurrentSectionIndex] = useState(0);
  const [showCancelAlert, setShowCancelAlert] = useState(false);
  const [warningLabels, setWarningLabels] = useState({}); // Initialize as object
  const [showOutputScreen, setShowOutputScreen] = useState(false); // New state
  /* ---------- diagnostics (safe) ---------- */
  console.log("sections being passed ", sections);
  console.log(
    "[InputFormAI] sections.length =",
    sections.length,
    "index =",
    currentSectionIndex,
    sections
  );

  // New states for loading and result
  const [loading, setLoading] = useState(false);
  const [loadingLabel, setLoadingLabel] = useState("");
  const [result, setResult] = useState(null); // { status: "success" | "error", title: string }

  useEffect(() => {
    if (!isNew && initialFormData) {
      setFormData(initialFormData);
    }
  }, [initialFormData, isNew]);

  const currentSection = sections[currentSectionIndex];
  const totalSections = sections.length;

  // Handle input changes for form fields
  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((prevData) => ({
      ...prevData,
      [name]: value,
    }));
  };

  // Function to handle cancellation
  const handleCancelClick = () => setShowCancelAlert(true);

  // Handle navigating to the next section based on section type
  const handleNextSection = () => {
    if (currentSectionIndex === totalSections - 1) {
      // Last section, handle submission
      handleSubmit();
    } else {
      // Not the last section, navigate to next
      navigateNext(
        currentSectionIndex,
        totalSections,
        setCurrentSectionIndex,
        validationRules, // Pass validationRules here
        formData,
        currentSection,
        setWarningLabels // Pass the setter function
      );
    }
  };

  // Handle navigating to the previous section
  const handleBack = () => {
    setCurrentSectionIndex((prevIndex) => prevIndex - 1);
  };

  // Function to handle updated data from Step9AuditReview
  const handleDataPointsUpdate = (updatedDataPoints, auditRiskProfile) => {
    setFormData((prevData) => ({
      ...prevData,
      ...updatedDataPoints,
      auditRiskProfile, // Add the auditRiskProfile to formData
    }));

    setResolvedFields((prevResolved) => [
      ...prevResolved,
      ...Object.keys(updatedDataPoints),
    ]);

    setCurrentSectionIndex((prevIndex) => prevIndex + 1);
  };

  // Utility function to flatten nested objects
  const flattenFormData = (data, parentKey = "", result = {}) => {
    for (let key in data) {
      if (data.hasOwnProperty(key)) {
        const newKey = parentKey ? `${parentKey}_${key}` : key;
        const value = data[key];

        if (
          typeof value === "object" &&
          !Array.isArray(value) &&
          value !== null
        ) {
          // Recursively flatten nested objects
          flattenFormData(value, newKey, result);
        } else if (Array.isArray(value)) {
          // Convert arrays to comma-separated strings
          result[newKey] = value.join(", ");
        } else {
          result[newKey] = value || "N/A"; // Handle undefined or null values
        }
      }
    }
    return result;
  };

  // Function to handle form submission
  const handleSubmit = () => {
    // Flatten the formData
    const finalFormData = flattenFormData(formData);
    console.log("Final Form Data:", finalFormData);

    // Prepare dataObjects with a single section
    const outputDataObjects = [
      {
        title: "Submission Details",
        data: finalFormData,
      },
    ];

    // Display OutputScreen with multiple data objects
    setShowOutputScreen(true);
  };
  // ─────────────────────────────────────────────────────────────────────────────
  //  REPLACEMENT: put this in InputFormAI.jsx (same place as the old function)
  // ─────────────────────────────────────────────────────────────────────────────
  const writeRecordToFirestore = async () => {
    console.log("-------------------------------------------------------");
    console.log("Initiating write to Firestore...");
    console.log("[writeRecordToFirestore] fired");
    console.log(
      "→ formData before flattening:",
      JSON.parse(JSON.stringify(formData))
    );

    try {
      // ───── 1. duplicate-guard via Cloud Function ─────
      setLoading(true);
      setLoadingLabel("Checking for duplicates…");
      console.log(
        "Checking for duplicates in prohibited fields:",
        checkDuplicateFields
      );

      // Build the payload the function expects: [{ fieldName, value }, …]
      const duplicateFields = checkDuplicateFields
        .filter(
          (f) =>
            duplicates[f] === "prohibited" && (formData[f] ?? "").trim() !== ""
        )
        .map((f) => ({ fieldName: f, value: formData[f] }));

      if (duplicateFields.length) {
        const checkDuplicates = httpsCallable(functions, "checkDuplicates");
        const { data } = await checkDuplicates({
          company,
          collectionBrand,
          systemName,
          fields: duplicateFields,
        });
        console.log("checkDuplicates response:", data);

        if (data?.duplicates?.length) {
          // A duplicate was found – show the same error UI as before
          const { fieldName, value } = data.duplicates[0]; // just show first for brevity
          setLoading(false);
          setLoadingLabel("");
          setResult({
            status: "error",
            title: "Duplicate detected",
            description:
              `Another record already has ${fieldName}: ${value}. ` +
              "Please correct it or contact support.",
          });
          return; // stop the flow
        }
      }

      // ───── 2. write via firestoreOperations Cloud Function ─────
      console.log("No duplicates found. Proceeding with write operation…");
      setLoadingLabel("Writing data to Firestore…");

      // Strip auditRiskProfile, then flatten nested objects / arrays
      const { auditRiskProfile, ...formDataWithoutAudit } = formData;
      console.log("Form data without auditRiskProfile:", formDataWithoutAudit);

      const finalFormData = flattenFormData(formDataWithoutAudit);
      console.log("Flattened Form Data:", finalFormData);

      const timestamp = new Date().toISOString();
      const documentData = {
        ...finalFormData,
        timeStamp: timestamp,
        createdBy: userEmail,
      };
      console.log("Document Data to write:", documentData);

      extractDataPoints({ ...documentData, uniqueId });

      const firestoreOperations = httpsCallable(
        functions,
        "firestoreOperations"
      );
      console.log("Calling firestoreOperations with operation 'update'…", {
        company,
        collectionBrand,
        systemName,
        uniqueId,
      });

      await firestoreOperations({
        operation: "update",
        company,
        collectionBrand,
        systemName,
        uniqueId,
        documentData,
      });

      console.log("Document successfully written to Firestore.");

      // (optional) audit-trail helper you already had
      await logUserAction(
        userEmail,
        systemName,
        "Add Record",
        uniqueId,
        timestamp
      );
      console.log("User action logged successfully.");

      // inside try{} right after “User action logged successfully.”
      dispatch(setLastWrittenUid(uniqueId));
      console.log(
        "[Redux] lastWrite slice after dispatch →",
        store.getState().lastWrite
      );

      /* ───────── DEBUG: confirm dispatch took effect ───────── */
      console.log("[InputFormAI] setLastWrittenUid dispatched:", uniqueId);
      console.log(
        "[InputFormAI] heroUser slice right now →",
        store.getState().heroUser // should now show lastWrittenUid: uniqueId
      );
      /* ──────────────────────────────────────────────────────── */

      setResult({
        status: "success",
        title: "Your record has been successfully updated.",
      });
    } catch (error) {
      // ╭──────────────────────────────────────────────────────────╮
      // │ Robust logging – see every detail of the failure         │
      // ╰──────────────────────────────────────────────────────────╯
      console.error("Error writing record to Firestore → raw:", error);
      if (error instanceof Error) {
        console.error("→ message:", error.message);
        console.error("→ code   :", error.code ?? "(none)");
        console.error("→ details:", error.details ?? "(none)");
      }
      if (error?.data?.error) {
        console.error("→ data.error payload:", error.data.error);
      }

      setResult({
        status: "error",
        title: "An error occurred while adding your record.",
      });
    } finally {
      console.log("Write operation complete. Resetting loading states.");
      setLoading(false);
      setLoadingLabel("");
    }
  };

  const extractRecord = () => {
    const { auditRiskProfile, ...formDataWithoutAudit } = formData;
    const finalFormData = flattenFormData(formDataWithoutAudit);
    extractDataPoints({ ...finalFormData, uniqueId });
  };

  useEffect(() => {
    if (!formData) return;
    extractRecord();
  }, [formData]);
  // ─────────────────────────────────────────────────────────────────────────────

  return (
    <div className="input-form-ai">
      {/* Full-screen overlay to prevent navigation */}
      {currentSectionIndex < sections.length && (
        <div className="full-screen-overlay" />
      )}

      {/* Information Box */}
      <InputFormAIInformationBox
        currentSectionIndex={currentSectionIndex}
        sections={sections}
        isNew={isNew}
        systemName={systemName}
        uniqueId={uniqueId}
        formData={formData}
        onCancel={handleCancelClick}
      />

      <>
        {loading ? (
          // Display LoadingCircle when loading is true
          <LoadingCircle label={loadingLabel || "Processing..."} />
        ) : result ? (
          // Display StatusScreen when result is available
          <StatusScreen
            title={result.title}
            status={result.status}
            onContinue={() => {
              // Handle continue button
              setResult(null);
              if (onComplete) {
                onComplete();
              }
            }}
          />
        ) : showOutputScreen ? (
          // Display OutputScreen when form submission is complete
          <OutputScreen
            title="Record Complete"
            info="Almost done. Click continue to check for duplicate records and complete"
            dataObjects={[
              {
                title: "Submission Details",
                data: flattenFormData(formData), // Pass the flattened data
              },
            ]}
            onContinue={writeRecordToFirestore} // Trigger write to Firestore
          />
        ) : (
          // Display the form sections or document upload
          <>
            {showCancelAlert && (
              <Alerts
                type="windowConfirm"
                message="Stopping the add record process will delete your progress and navigate you back to the console dashboard. Are you sure?"
                onConfirm={() => {
                  setShowCancelAlert(false);
                  window.location.replace(`/hero-dashboard/${systemName}`);
                  handleCancel(
                    company,
                    collectionBrand,
                    systemName,
                    uniqueId,
                    setLoadingLabel,
                    setResult,
                    () => {}
                  );
                }}
                onCancel={() => setShowCancelAlert(false)}
              />
            )}
            {currentSection.isDocumentUpload ? (
              <UploadDocument
                userEmail={userEmail}
                currentSection={currentSection}
                company={company}
                collectionBrand={collectionBrand}
                systemName={systemName}
                uniqueId={uniqueId}
                dataPointDefinitions={dataPointDefinitions}
                onSuccess={handleNextSection}
                onDataPointsUpdate={handleDataPointsUpdate}
                formData={formData}
              />
            ) : (
              <DynamicFormSection
                section={currentSection}
                formData={formData}
                resolvedFields={resolvedFields}
                onInputChange={handleInputChange}
                onBack={handleBack}
                onNext={handleNextSection}
                isLastSection={currentSectionIndex === sections.length - 1}
                currentSectionIndex={currentSectionIndex}
                warningLabels={warningLabels} // Pass the warningLabels state as object
              />
            )}
          </>
        )}
      </>
    </div>
  );
};

export default InputFormAI;
